/* eslint-disable simple-import-sort/imports */
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";

import { AvailableData, ExtractedMO, aiUploadApi } from "../../../../../services/ai-upload-api";
import { GhostButton,planner,PrimaryButton } from "../../../planner-ui"; // eslint-disable-line sort-imports
import { ManufacturingOrder } from "../../utils/calculations";
import ProcessingStep from "./ProcessingStep";
import ReviewStep from "./ReviewStep";
import UploadStep from "./UploadStep";

interface AIUploadDialogProps {
    open: boolean
    onClose: () => void
    onSuccess: (orders: ManufacturingOrder[]) => void
    weekStartDate: string
    products: { id: number; sku: string; name: string }[]
    workcenters: string[]
}

const getSteps = (hasSheetSelection: boolean) =>
    hasSheetSelection
        ? ["Upload Document", "Select Sheet", "Processing", "Review & Edit"]
        : ["Upload Document", "Processing", "Review & Edit"];

const AIUploadDialog: React.FC<AIUploadDialogProps> = ({
    open,
    onClose,
    onSuccess,
    weekStartDate,
    products,
    workcenters,
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [processing, setProcessing] = useState(false);
    const [extractedOrders, setExtractedOrders] = useState<ExtractedMO[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);
    // Sheet selection state
    const [availableSheets, setAvailableSheets] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState<string>("");
    const [showSheetSelection, setShowSheetSelection] = useState(false);

    const steps = getSteps(showSheetSelection);

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        setError(null);
        setAvailableSheets([]);
        setSelectedSheet("");
        setShowSheetSelection(false);
    };

    const handleStartProcessing = async (sheetName?: string) => {
        if (!selectedFile) return;

        // Go to processing step
        setActiveStep(showSheetSelection ? 2 : 1);
        setProcessing(true);
        setError(null);

        try {
            const availableData: AvailableData = {
                products: products.map((p) => ({ id: p.id, sku: p.sku, name: p.name })),
                workcenters,
            };

            const response = await aiUploadApi.parseDocument(
                selectedFile,
                weekStartDate,
                availableData,
                sheetName || selectedSheet || undefined
            );

            if (response.success) {
                // Check if we need to select a sheet first
                if (response.requiresSheetSelection && response.availableSheets && response.availableSheets.length > 0) {
                    setAvailableSheets(response.availableSheets);
                    setSelectedSheet(response.availableSheets[0]); // Default to first sheet
                    setShowSheetSelection(true);
                    setActiveStep(1); // Go to sheet selection step
                    setProcessing(false);
                    return;
                }

                setExtractedOrders(response.extractedOrders);
                setWarnings(response.warnings || []);
                setActiveStep(showSheetSelection ? 3 : 2); // Go to review step
            } else {
                setError(response.error || "Failed to parse document");
                setActiveStep(0);
            }
        } catch (err: any) {
            console.error("AI parsing error:", err);
            setError(
                err?.response?.data?.error ||
                err?.message ||
                "Failed to process document. Please try again."
            );
            setActiveStep(0);
        } finally {
            setProcessing(false);
        }
    };

    const handleProcessWithSheet = () => {
        if (selectedSheet) {
            handleStartProcessing(selectedSheet);
        }
    };

    const handleOrdersChange = (orders: ExtractedMO[]) => {
        setExtractedOrders(orders);
    };

    // Check for conflicts within the batch (same workcenter with overlapping times)
    const detectBatchConflicts = (orders: ExtractedMO[]): string[] => {
        const conflicts: string[] = [];
        const validOrders = orders.filter((o) => o.isValid);

        for (let i = 0; i < validOrders.length; i++) {
            for (let j = i + 1; j < validOrders.length; j++) {
                const order1 = validOrders[i];
                const order2 = validOrders[j];

                // Skip if different workstations
                if (order1.workstation !== order2.workstation) continue;

                // Skip if missing times
                if (!order1.startTime || !order2.startTime) continue;

                // Check for time overlap
                const start1 = new Date(order1.startTime);
                const end1 = order1.endTime ? new Date(order1.endTime) : new Date(start1.getTime() + 8 * 60 * 60 * 1000);
                const start2 = new Date(order2.startTime);
                const end2 = order2.endTime ? new Date(order2.endTime) : new Date(start2.getTime() + 8 * 60 * 60 * 1000);

                const hasOverlap = (
                    (start1 >= start2 && start1 < end2) ||
                    (end1 > start2 && end1 <= end2) ||
                    (start1 <= start2 && end1 >= end2)
                );

                if (hasOverlap) {
                    conflicts.push(
                        `${order1.sku} and ${order2.sku} conflict on ${order1.workstation}: ` +
                        `${format(start1, "MMM d HH:mm")}-${format(end1, "HH:mm")} overlaps with ` +
                        `${format(start2, "MMM d HH:mm")}-${format(end2, "HH:mm")}`
                    );
                }
            }
        }

        return conflicts;
    };

    const handleConfirm = () => {
        // Check for conflicts before submitting
        const conflicts = detectBatchConflicts(extractedOrders);
        if (conflicts.length > 0) {
            setError(
                `Found ${conflicts.length} conflict(s):\n` +
                conflicts.slice(0, 5).join("\n") +
                (conflicts.length > 5 ? `\n...and ${conflicts.length - 5} more` : "") +
                "\n\nPlease adjust the times to avoid overlap."
            );
            return;
        }

        const validOrders = aiUploadApi.convertToManufacturingOrders(extractedOrders);
        onSuccess(validOrders as ManufacturingOrder[]);
        handleClose();
    };

    const handleClose = () => {
        setActiveStep(0);
        setSelectedFile(null);
        setExtractedOrders([]);
        setError(null);
        setWarnings([]);
        setProcessing(false);
        setAvailableSheets([]);
        setSelectedSheet("");
        setShowSheetSelection(false);
        onClose();
    };

    const handleBack = () => {
        const reviewStep = showSheetSelection ? 3 : 2;
        const sheetStep = 1;

        if (activeStep === reviewStep) {
            // Go back to upload step
            setActiveStep(0);
            setExtractedOrders([]);
        } else if (activeStep === sheetStep && showSheetSelection) {
            // Go back to upload step
            setActiveStep(0);
        }
    };

    // Determine which step we're on for rendering
    const isUploadStep = activeStep === 0;
    const isSheetSelectionStep = showSheetSelection && activeStep === 1;
    const isProcessingStep = showSheetSelection ? activeStep === 2 : activeStep === 1;
    const isReviewStep = showSheetSelection ? activeStep === 3 : activeStep === 2;

    const validOrderCount = extractedOrders.filter((o) => o.isValid).length;

    return (
        <Dialog
            open={open}
            onClose={processing ? undefined : handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "500px",
                    borderRadius: `${planner.radius}px`,
                    boxShadow: planner.shadowDialog,
                },
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <FactCheckOutlinedIcon sx={{ color: planner.navy }} />
                    <Typography sx={{ ...planner.type.title, color: planner.navy }}>
                        Upload work plan
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Stepper
                    activeStep={activeStep}
                    sx={{
                        mb: 3,
                        "& .MuiStepLabel-label": {
                            ...planner.type.metaStrong,
                            color: planner.inkMuted,
                        },
                        "& .MuiStepLabel-label.Mui-active": { color: planner.navy },
                        "& .MuiStepLabel-label.Mui-completed": { color: planner.inkSecondary },
                        "& .MuiStepConnector-line": { borderColor: planner.hairlineStrong },
                    }}
                >
                    {steps.map((label, index) => (
                        <Step key={label} completed={activeStep > index}>
                            <StepLabel
                                StepIconComponent={() => {
                                    if (activeStep > index) {
                                        return <CheckCircleIcon sx={{ color: planner.staffed }} />;
                                    }
                                    // Brick marks the active step; inactive steps stay muted ink.
                                    const stepIconSx = (active: boolean) => ({
                                        color: active ? planner.accent : planner.hairlineStrong,
                                    });
                                    if (label === "Upload Document") return <CloudUploadIcon sx={stepIconSx(isUploadStep)} />;
                                    if (label === "Select Sheet") return <DescriptionIcon sx={stepIconSx(isSheetSelectionStep)} />;
                                    if (label === "Processing") return <FactCheckOutlinedIcon sx={stepIconSx(isProcessingStep)} />;
                                    return <CheckCircleIcon sx={stepIconSx(isReviewStep)} />;
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert
                        severity="error"
                        icon={false}
                        sx={{
                            ...planner.type.body,
                            mb: 2,
                            whiteSpace: "pre-wrap",
                            borderRadius: `${planner.radiusSm}px`,
                            border: `${planner.borderWidth}px solid ${planner.unstaffed}55`,
                            bgcolor: planner.unstaffedSoft,
                            color: planner.unstaffed,
                        }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {warnings.length > 0 && isReviewStep && (
                    <Alert
                        severity="warning"
                        icon={false}
                        sx={{
                            mb: 2,
                            borderRadius: `${planner.radiusSm}px`,
                            border: `${planner.borderWidth}px solid ${planner.partial}55`,
                            bgcolor: planner.partialSoft,
                            color: planner.partial,
                        }}
                    >
                        {warnings.map((w, i) => (
                            <Typography key={i} sx={{ ...planner.type.body, color: planner.partial }}>{w}</Typography>
                        ))}
                    </Alert>
                )}

                {isUploadStep && (
                    <UploadStep
                        selectedFile={selectedFile}
                        onFileSelect={handleFileSelect}
                        onRemoveFile={() => setSelectedFile(null)}
                    />
                )}

                {isSheetSelectionStep && (
                    <Box sx={{ py: 2 }}>
                        <Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
                            This workbook has multiple sheets
                        </Typography>
                        <Typography sx={{ ...planner.type.body, color: planner.inkSecondary, mb: 3 }}>
                            Please select which sheet contains your production plan:
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={selectedSheet}
                                onChange={(e) => setSelectedSheet(e.target.value)}
                            >
                                {availableSheets.map((sheet) => (
                                    <FormControlLabel
                                        key={sheet}
                                        value={sheet}
                                        control={<Radio />}
                                        label={sheet}
                                        sx={{
                                            border: `${planner.borderWidth}px solid`,
                                            borderColor:
                                                selectedSheet === sheet ? planner.accent : planner.hairlineStrong,
                                            borderRadius: `${planner.radiusSm}px`,
                                            mb: 1,
                                            px: 2,
                                            py: 0.5,
                                            bgcolor:
                                                selectedSheet === sheet ? planner.accentSoft : planner.surface,
                                            "&:hover": { borderColor: planner.navy },
                                            "& .MuiFormControlLabel-label": {
                                                ...planner.type.body,
                                                color: planner.ink,
                                            },
                                            "& .MuiRadio-root": { color: planner.inkMuted },
                                            "& .MuiRadio-root.Mui-checked": { color: planner.accent },
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                {isProcessingStep && (
                    <ProcessingStep fileName={selectedFile?.name || ""} />
                )}

                {isReviewStep && (
                    <ReviewStep
                        extractedOrders={extractedOrders}
                        products={products}
                        workcenters={workcenters}
                        weekStartDate={weekStartDate}
                        onOrdersChange={handleOrdersChange}
                    />
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    pt: 2,
                    borderTop: `${planner.borderWidth}px solid ${planner.hairline}`,
                }}
            >
                {isUploadStep && (
                    <>
                        <GhostButton size="small" onClick={handleClose}>Cancel</GhostButton>
                        <PrimaryButton
                            size="small"
                            onClick={() => handleStartProcessing()}
                            disabled={!selectedFile}
                            startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
                        >
                            Read the document
                        </PrimaryButton>
                    </>
                )}

                {isSheetSelectionStep && (
                    <>
                        <GhostButton size="small" onClick={handleBack}>Back</GhostButton>
                        <GhostButton size="small" onClick={handleClose}>Cancel</GhostButton>
                        <PrimaryButton
                            size="small"
                            onClick={handleProcessWithSheet}
                            disabled={!selectedSheet}
                            startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
                        >
                            Process Selected Sheet
                        </PrimaryButton>
                    </>
                )}

                {isProcessingStep && (
                    <GhostButton size="small" disabled>Processing...</GhostButton>
                )}

                {isReviewStep && (
                    <>
                        <GhostButton size="small" onClick={handleBack}>Back</GhostButton>
                        <GhostButton size="small" onClick={handleClose}>Cancel</GhostButton>
                        <PrimaryButton
                            size="small"
                            onClick={handleConfirm}
                            disabled={validOrderCount === 0}
                            startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        >
                            Upload {validOrderCount} Order{validOrderCount !== 1 ? "s" : ""}
                        </PrimaryButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AIUploadDialog;

